import { cn } from '@/lib/utils';
import { formatRelativeTime, formatAbsoluteTime } from '@/lib/formatRelativeTime';
import { rowDescriptor } from '../lib/kindMap';
import { ActivitySeverity, type ActivityLogDto } from '../types';
import { EvidenceBlock } from './panel/evidence';

interface ActivityRowProps {
  entry: ActivityLogDto;
  onSelect: () => void;
  /** Show the EntityType + EntityLabel context inline. Defaults to false (rail
   *  variant — caller already knows the entity). Set true on the cross-module
   *  admin feed where rows span entities. */
  showEntity?: boolean;
}

/**
 * A row on the ADMIN feed (`/activity`).
 *
 * ## What changed, and what deliberately did not
 *
 * The severity bullet is replaced by the **kind tile** — the same icon and tint the docked panel uses, from
 * the same map. Severity has not been deleted: it still decides a reason tag's tone below, which is the job
 * it is actually good at. What it was doing here was standing in for a render axis that did not exist yet.
 *
 * The row now renders **`entry.evidence`** — the server's ready-to-draw blocks — instead of a client-side
 * per-ActionType parser.
 *
 * ⚠️ **That parser is deleted, and it was already dead.** `describeActivity` had exactly one special case,
 * `PicklistAdjusted`, reading `binAdjustments[{oldQuantity,newQuantity}]`. B12 renamed those keys to
 * `bins[{binCode,from,to}]`, so for every row written since it returned null and silently fell through to
 * plain text. Nobody noticed, because a renderer that degrades quietly looks exactly like one that has
 * nothing to render. It is the founding example of why the client resolves no payload keys.
 *
 * The **relative timestamp stays** — the admin feed is a cross-module surface people scan for "what
 * happened recently", where "3 hours ago" beats a wall clock. The panel uses an absolute PHT time because
 * its day is already established by a sticky header. Two surfaces, two correct answers.
 */
export function ActivityRow({ entry, onSelect, showEntity = false }: ActivityRowProps) {
  const relative = formatRelativeTime(entry.occurredAtUtc);
  const absolute = formatAbsoluteTime(entry.occurredAtUtc);
  const { icon: Icon, tile } = rowDescriptor(entry);
  const tone = severityTone(entry.severity);

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'group flex w-full items-start gap-2.5 px-3 py-2 text-left text-sm',
          'rounded transition-colors hover:bg-accent',
        )}
      >
        <span
          aria-hidden
          className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md', tile)}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>

        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div className="min-w-0 flex-1 space-y-1.5 leading-snug">
            <p className="break-words text-foreground">
              <span className="text-muted-foreground">{entry.actorSnapshot}</span>{' '}
              <span className="text-foreground">{entry.summary}</span>
              {showEntity && entry.entityLabel && (
                <span className="text-muted-foreground"> · {entry.entityLabel}</span>
              )}
            </p>

            {/* Ordered detail-first, reason-last by the server (§C4) and rendered in array order. Keyed by
                index because a block has no identity of its own — the array is rebuilt on every fetch. */}
            {entry.evidence.map((block, i) => (
              <EvidenceBlock key={i} evidence={block} tone={tone} />
            ))}
          </div>

          <time
            dateTime={entry.occurredAtUtc}
            title={absolute}
            className="shrink-0 whitespace-nowrap pt-px text-xs text-muted-foreground group-hover:text-foreground"
          >
            {relative}
          </time>
        </div>
      </button>
    </li>
  );
}

/** Severity → the tone a reason tag paints in. Only these two carry meaning; Info gets no colour. */
function severityTone(severity: ActivitySeverity): string | null {
  if (severity === ActivitySeverity.Critical) return 'destructive';
  if (severity === ActivitySeverity.Notable) return 'warning';
  return null;
}
